import React, { useRef, useCallback, useState, useEffect, useLayoutEffect, useId } from 'react';
import Map, { Source, Layer, NavigationControl, MapRef, MapLayerMouseEvent } from 'react-map-gl';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ReactComponent as MapIcon } from "../../assets/icons/map-02.svg";
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe-02.svg";
import useIsMobile from '../../hooks/useIsMobile';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiY3JhaWdtYWMwMDciLCJhIjoiY2xkbHNlZ3h1MDE2bjNxcGJjYWo5YzRtMCJ9.ezesyu3cASMlnZlJLMkZwg';

// Width an assistant map takes when nothing else in the bubble anchors it
const MAP_FALLBACK_WIDTH_DESKTOP = 560;
const MAP_FALLBACK_WIDTH_MOBILE = 300;
// Sent-message maps follow their bubble, so they only need a floor to stay legible
const FIT_PARENT_MIN_WIDTH_DESKTOP = 260;
const FIT_PARENT_MIN_WIDTH_MOBILE = 180;

interface MiniMapProps {
  geojson: GeoJSON.FeatureCollection;
  title?: string;
  fixedHeight: number; // If true, the map container is a fixed width (290px desktop, 260px mobile)
  lockWidth?: boolean;
  fitParent?: boolean; // Follow the parent's width — for bubbles that already have their own width anchor
}

interface PopupInfo {
  properties: Record<string, any>;
}

const MiniMap: React.FC<MiniMapProps> = ({ geojson, title, fixedHeight, lockWidth, fitParent }) => {
  const mapRef = useRef<MapRef>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const isMobile = useIsMobile();
  // First-paint fallback: a % width contributes ~0 to the shrink-to-fit chat bubble, so anchor it in px
  const [containerWidth, setContainerWidth] = useState<number>(isMobile ? MAP_FALLBACK_WIDTH_MOBILE : MAP_FALLBACK_WIDTH_DESKTOP);
  // Sizing is a single mode, not two independent flags — lockWidth wins so a fixed card is never overridden
  const sizingMode: 'lock' | 'fit' | 'measure' = lockWidth ? 'lock' : fitParent ? 'fit' : 'measure';

  // Map style state
  const LIGHT_STYLE = "mapbox://styles/mapbox/light-v11";
  const SATELLITE_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";
  const [mapStyle, setMapStyle] = useState(LIGHT_STYLE);

  const uid = useId();
  const sourceId = `mini-map-source${uid}`;
  const fillId = `mini-map-fill${uid}`;
  const lineId = `mini-map-line${uid}`;
  const circleId = `mini-map-circle${uid}`;
  const circleSelectedId = `mini-map-circle-selected${uid}`;
  const labelsId = `mini-map-labels${uid}`;

  const handleClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    if (!feature) { setPopupInfo(null); return; }

    const { _color, ...visibleProps } = feature.properties || {};
    setPopupInfo({ properties: visibleProps });
  }, []);

  const handleMapLoad = useCallback(() => {
    if (!mapRef.current || !geojson.features.length) return;
    try {
      const bbox = turf.bbox(geojson);
      // maxZoom keeps degenerate extents (a single asset point) at a regional
      // view instead of Mapbox's max zoom.
      mapRef.current.fitBounds(
        [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
        { padding: 40, duration: 0, maxZoom: 9 }
      );
      
      // Force resize after a short delay to handle container dimension issues
      setTimeout(() => {
        mapRef.current?.resize();
      }, 100);
    } catch {
      // fallback: leave default view
    }
  }, [geojson]);

  // Trigger a resize once the container has settled, to handle edge cases where
  // the map initialises before its fixed-size box is laid out.
  useEffect(() => {
    const resizeTimer = setTimeout(() => {
      mapRef.current?.resize();
    }, 200);

    return () => clearTimeout(resizeTimer);
  }, [fixedHeight]);

  // Bubble width changes as text streams in above the map, so watch the box rather than just the window.
  // Layout effect so the measured width replaces the fallback before the first paint, avoiding a width flash.
  useLayoutEffect(() => {
    if (sizingMode === 'lock') return;
    const target = wrapperRef.current;
    if (!target || typeof ResizeObserver === 'undefined') return;

    const update = () => {
      // fitParent follows the parent's width in CSS, so the canvas just needs to catch up
      if (sizingMode === 'fit') {
        mapRef.current?.resize();
        return;
      }
      const measured = target.offsetWidth;
      if (measured) setContainerWidth(measured);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(target);
    return () => observer.disconnect();
  }, [sizingMode]);

  useEffect(() => {
    mapRef.current?.resize();
  }, [containerWidth]);

  // Catch a caller passing both sizing flags — lockWidth silently wins otherwise
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && lockWidth && fitParent) {
      console.warn('MiniMap: lockWidth and fitParent are mutually exclusive — falling back to lockWidth.');
    }
  }, [lockWidth, fitParent]);

  return (
    // Unsized wrapper resolves to the bubble's content width; the card inside carries the px width
    <div ref={wrapperRef} className="w-full mb-4 last:mb-0">
    <div
      className="bg-gray-50 rounded-lg border border-gray-200 relative overflow-hidden"
      style={
        sizingMode === 'lock'
          ? { width: isMobile ? 260 : 290, maxWidth: '100%' }
          : sizingMode === 'fit'
          ? { width: '100%', minWidth: isMobile ? FIT_PARENT_MIN_WIDTH_MOBILE : FIT_PARENT_MIN_WIDTH_DESKTOP }
          : { width: containerWidth, maxWidth: '100%' }
      }
    >
      <div style={{ height: fixedHeight, width: '100%' }} className="relative">
        <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={mapStyle}
          initialViewState={{ longitude: 0, latitude: 20, zoom: 1 }}
          onLoad={handleMapLoad}
          onClick={handleClick}
          interactiveLayerIds={[fillId, lineId, circleId]}
        >
          <NavigationControl position="top-right" showCompass={false} />
          
          {/* Map Style Toggle */}
          <div className="absolute z-10" style={{ top: title ? '54px' : '10px', left: '10px' }}>
            <div className="flex bg-white rounded-md overflow-hidden border border-gray-200 shadow-sm">
              <button
                className={`p-1 transition-colors ${mapStyle === LIGHT_STYLE ? "bg-gray-200" : "bg-white hover:bg-gray-100"}`}
                onClick={() => setMapStyle(LIGHT_STYLE)}
                title="Map View"
              >
                <MapIcon className="w-5 h-5" />
              </button>
              <button
                className={`p-1 transition-colors ${mapStyle === SATELLITE_STYLE ? "bg-gray-200" : "bg-white hover:bg-gray-100"}`}
                onClick={() => setMapStyle(SATELLITE_STYLE)}
                title="Satellite View"
              >
                <GlobeIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <Source id={sourceId} type="geojson" data={geojson}>
            <Layer
              id={fillId}
              type="fill"
              filter={['==', '$type', 'Polygon']}
              paint={{ 'fill-color': '#7BA428', 'fill-opacity': 0.25 }}
            />
            <Layer
              id={lineId}
              type="line"
              filter={['in', '$type', 'Polygon', 'LineString']}
              paint={{ 'line-color': '#7BA428', 'line-width': 2 }}
            />
            <Layer
              id={circleId}
              type="circle"
              filter={['==', '$type', 'Point']}
              paint={{
                'circle-radius': 6,
                'circle-color': '#7BA428',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
              }}
            />
            <Layer
              id={circleSelectedId}
              type="circle"
              filter={popupInfo?.properties?.name
                ? ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'name'], popupInfo.properties.name]]
                : ['boolean', false]}
              paint={{
                'circle-radius': 9,
                'circle-color': '#5a8a1e',
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffffff',
              }}
            />
            <Layer
              id={labelsId}
              type="symbol"
              layout={{
                'text-field': ['get', 'name'],
                'text-size': 12,
                'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                'text-radial-offset': 0.8,
                'text-justify': 'auto',
              }}
              paint={{
                'text-color': '#1f2937',
                'text-halo-color': '#ffffff',
                'text-halo-width': 2,
              }}
            />
          </Source>
        </Map>
        
        {/* Title overlay */}
        {title && (
          <div className="absolute z-20" style={{ top: '10px', left: '10px' }}>
            <div className="bg-white rounded-md px-3 py-2 shadow-sm border border-gray-200">
              <h4 className="text-sm font-extrabold text-black truncate">{title}</h4>
            </div>
          </div>
        )}
        {/* Feature info panel — floats over the bottom-left of the map when a feature is clicked. */}
        {popupInfo && (
          <div className="absolute bottom-3 left-3 z-30 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-md animate-slideUp font-['Inter'] shadow-sm max-w-[280px]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {popupInfo.properties.name ?? 'Feature'}
              </span>
              <button
                onClick={() => setPopupInfo(null)}
                className="ml-2 flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5 max-h-36 overflow-y-auto">
              {Object.entries(popupInfo.properties)
                .filter(([key]) => key !== 'name')
                .map(([key, val]) => (
                  <div key={key} className="min-w-0">
                    <span className="text-xs uppercase tracking-wide text-gray-400 block">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-800 font-medium leading-tight break-words">{String(val)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

      </div>
    </div>
    </div>
  );
};

export default MiniMap;
